/**
 * SweepGroupNode — custom React Flow node for a parameter sweep group.
 *
 * Renders collapsed (stacked card) or expanded (bordered box with run chips)
 * based on `data.expanded`. One component, two modes.
 *
 * Subscribes to ProvenanceContext directly for selection state so that
 * highlight updates are immediate — bypassing React Flow's data pipeline
 * which can lag by a render cycle.
 */

import { memo, useMemo, type CSSProperties } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Tooltip } from "@radix-ui/themes";
import type { SweepGroup } from "./dagGrouping";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { sweepTooltipContent } from "./dagTooltips";
import { CrispNodeContent } from "./CrispNodeContent";

interface SweepGroupData {
  label: string;
  group: SweepGroup;
  expanded: boolean;
  onToggle: () => void;
  onSelectRun?: (nodeId: string) => void;
  /**
   * When false, the expand affordance is hidden and clicking does nothing.
   * Used for sweep groups larger than the expansion cap (layout would be
   * unusable at that scale).
   */
  expandable?: boolean;
  [key: string]: unknown;
}

const stackStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.15)",
  pointerEvents: "none",
};

function SweepGroupNodeInner({ data }: NodeProps) {
  const { selectedArtifactId } = useProvenanceContext();
  const { label, group, expanded, onToggle, onSelectRun, expandable = true } = data as unknown as SweepGroupData;
  const tooltipText = useMemo(() => sweepTooltipContent(group), [group]);

  if (expanded) {
    const cols = Math.min(4, Math.max(2, Math.ceil(Math.sqrt(group.runs.length))));
    return (
      <>
        <Handle type="target" position={Position.Top} style={{ opacity: 0, top: 0 }} />
        <CrispNodeContent width="100%" height="100%">
        <div
          style={{
            background: "rgba(34,197,94,0.03)",
            border: "2px dashed rgba(34,197,94,0.3)",
            borderRadius: 12,
            padding: 12,
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
              gap: 8,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
            <button
              onClick={onToggle}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                padding: "2px 4px",
                opacity: 0.7,
                color: "inherit",
              }}
              title="Collapse sweep group"
            >
              ▴
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: 4,
              flex: 1,
              alignContent: "start",
            }}
          >
            {group.smartNames.map((name, i) => {
              const run = group.runs[i];
              const isSelected = run?.id === selectedArtifactId;
              return (
                <span
                  key={run?.id ?? i}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (run && onSelectRun) onSelectRun(run.id);
                  }}
                  style={{
                    fontSize: 10,
                    background: isSelected ? "rgba(251,191,36,0.45)" : "rgba(34,197,94,0.15)",
                    border: isSelected ? "2px solid #fbbf24" : "1px solid transparent",
                    borderRadius: 4,
                    padding: isSelected ? "3px 7px" : "4px 8px",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                    boxShadow: isSelected ? "0 0 6px rgba(251,191,36,0.4)" : "none",
                  }}
                >
                  {name}
                </span>
              );
            })}
          </div>
        </div>
        </CrispNodeContent>
        <Handle type="source" position={Position.Bottom} style={{ opacity: 0, bottom: 0 }} />
      </>
    );
  }

  return (
    <>
    <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
    <CrispNodeContent width="100%" height="100%" style={{ overflow: "visible" }}>
    <Tooltip content={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>} delayDuration={400}>
    <div
      style={{ position: "relative", cursor: expandable ? "pointer" : "default", width: "100%" }}
      onClick={expandable ? onToggle : undefined}
      title={expandable ? undefined : `Sweep of ${group.runs.length} runs — too large to expand inline. Use the data table to inspect individual runs.`}
    >
      <div style={{ ...stackStyle, transform: "translate(4px, 4px)", background: "rgba(34,197,94,0.25)" }} />
      <div style={{ ...stackStyle, transform: "translate(2px, 2px)", background: "rgba(34,197,94,0.4)" }} />

      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1.3,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
          <span style={{ fontSize: 12, opacity: expandable ? 0.7 : 0.35 }}>{expandable ? "▾" : "▦"}</span>
        </div>

        {group.runs.length <= 8 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
            {group.smartNames.map((name, i) => (
              <span
                key={i}
                style={{
                  fontSize: 9,
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: 3,
                  padding: "1px 4px",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
    </Tooltip>
    </CrispNodeContent>
    <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </>
  );
}

export const SweepGroupNode = memo(SweepGroupNodeInner);
export default SweepGroupNode;
